import { useSubscription } from '@apollo/client';
import type { ChangeRequestPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetChangeRequestByParentIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useIsFeatureFlagEnabled } from 'src/hooks/useIsFeatureFlagEnabled';
import { useTRPC } from 'src/utils/trpc';

import { mapChangeRequestRowsToFragment } from './mapChangeRequestRowsToFragment';

/**
 * The async Pending→Approved/Rejected flip is driven by OTHER actors (the merge
 * consumer / other approvers), so client-side invalidation can't observe it —
 * the tRPC feed is POLLED. `refetchOnWindowFocus` is globally off, so this poll
 * is the live mechanism. 5s keeps the panel responsive without hammering the
 * single SAM-local data-layer container in local/ephemeral envs.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Approval-levels panel CR feed (RSP-6087). Returns every change request for a
 * parent (any status) in the GraphQL `ChangeRequestParts` shape. Flag-gated:
 *
 *   - `trpc` ON  → polls `frontend.changeRequest.changeRequestsByParent`. The
 *     Hasura WebSocket subscription does not deliver in the V3 stack (the
 *     approval-levels panel span forever), so a polled tRPC query replaces it.
 *   - `trpc` OFF → the legacy Hasura `getChangeRequestByParentId` subscription.
 *
 * Mirrors the subscription's `data.change_request` array so the consumer's
 * selection / dropdown / flip-detection logic is unchanged.
 */
export const useGetChangeRequestsByParent = (
  parentId: string
): {
  change_request: ChangeRequestPartsFragment[] | undefined;
  loading: boolean;
} => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const trpcQuery = useQuery({
    ...trpc.frontend.changeRequest.changeRequestsByParent.queryOptions({
      parentId,
    }),
    enabled: trpcEnabled && !!parentId,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const { data: subscriptionData, loading: subscriptionLoading } =
    useSubscription(GetChangeRequestByParentIdDocument, {
      variables: { Id: parentId },
      skip: trpcEnabled || !parentId,
    });

  // Memoise the mapped result: `mapChangeRequestRowsToFragment` produces a NEW
  // array every call, and consumers feed `change_request` into a `useEffect`
  // dependency list — without a stable reference the effect fires on every
  // render and `setSelectedChangeRequest` loops infinitely (the panel hangs
  // when anything inside it re-renders, e.g. typing in the comment box).
  // tanstack's structural sharing keeps `trpcQuery.data` stable between
  // identical polls, so this only recomputes when the CR data actually changes.
  const mappedChangeRequests = useMemo(
    () =>
      trpcQuery.data ? mapChangeRequestRowsToFragment(trpcQuery.data) : undefined,
    [trpcQuery.data]
  );

  if (trpcEnabled) {
    return {
      change_request: mappedChangeRequests,
      loading: trpcQuery.isLoading,
    };
  }

  return {
    change_request: subscriptionData?.change_request,
    loading: subscriptionLoading,
  };
};
